import { QueryResult } from '@apollo/client';
import { useCallback, useMemo, useState } from 'react';
import { Exact, Maybe } from '../../../../__generated__/globalTypes';
import {
  ProductPhotoReviewsQuery,
  useProductPhotoReviewsQuery,
} from './__generated__/ProductPhotoReviews';
import { getUniqueImages, ReviewPhotos } from './util';

type FetchMore = (incrementCount?: boolean) => Promise<void>;

type UseReviewPhotos = {
  /** Unique images */
  images: ReviewPhotos[];
  /** Whether data is currently being loaded from the endpoint */
  loading: boolean;
  /** Fetch more images, either from the local cache or, if needed, from the endpoint */
  fetchMore: FetchMore;
  /** Whether the are more images to be retrieved from the endpoint */
  hasMoreImages: boolean;
  /** Current max number of images that will be returned to the UI */
  maxImages: number;
  /** Apollo Query Result */
  query: QueryResult<
    ProductPhotoReviewsQuery,
    Exact<{
      id: string;
      after?: Maybe<string> | undefined;
      first?: Maybe<number> | undefined;
    }>
  >;
};

/**
 *
 * @param id product id
 * @param count numbers of additional images to return with each retrieval
 * @param ssr whether to fetch data during SSR
 * @returns
 */
const useReviewPhotos = (id: string, count: number = 15): UseReviewPhotos => {
  const [fetchCount, setFetchCount] = useState(1);
  const maxImages = fetchCount * count;
  const query = useProductPhotoReviewsQuery({
    variables: {
      id,
      // Keep in mind, this is the number of reviews with images, and each review will have 1 to 6 images
      // So for a fetch of 20, that means 1 to 120 images.
      first: 20,
    },
  });
  const { data, loading, fetchMore: fetchMoreQuery } = query;

  const allCurrentImages = useMemo(() => getUniqueImages(data?.product?.reviews?.edges), [
    data?.product?.reviews?.edges,
  ]);
  const { endCursor, hasNextPage } = data?.product?.reviews?.pageInfo ?? {};
  const hasMoreImages = hasNextPage || allCurrentImages.length > maxImages;

  /**
   * @param incrementCount whether to increment the number of images returned
   */
  const fetchMore: FetchMore = useCallback(
    async (incrementCount = true) => {
      // If we need more images and we are not already loading them, fetch
      if (allCurrentImages.length < maxImages && !loading) {
        await fetchMoreQuery({ variables: { id, after: endCursor } });
      }
      incrementCount && setFetchCount(fetchCount + 1);
    },
    [id, fetchCount, endCursor, maxImages, allCurrentImages, fetchMoreQuery, loading],
  );

  /**
   * Unique images from reviews
   */
  const images: ReviewPhotos[] = useMemo(() => {
    // If there are already enough images locally, return the desired count
    if (allCurrentImages.length > maxImages) {
      return allCurrentImages.slice(0, maxImages);
    }
    // Pre-fetch additional images if more are available
    if (hasNextPage) {
      fetchMore(false);
    }
    return allCurrentImages;
  }, [maxImages, allCurrentImages, hasNextPage, fetchMore]);

  return { images, loading, fetchMore, hasMoreImages, maxImages, query };
};

export default useReviewPhotos;
